export const AI_TESTCASE_TASK_STORAGE_PREFIX = "BUAAOJ_AI_TESTCASE_TASK_";
export const AI_TESTCASE_TASK_UPDATE_EVENT = "ai-testcase-task-updated";
export const AI_TESTCASE_REVIEW_TASK_ID = "BUAAOJ_AI_TESTCASE_REVIEW_TASK_ID";
export const AI_TESTCASE_REVIEW_EVENT = "ai-testcase-review-requested";

export function buildEmptyAiTestcaseTask() {
  return {
    taskId: "",
    status: "",
    errorCode: "",
    errorMessage: "",
    contestId: "",
    problemId: "",
    problemName: "",
    problemNumber: "",
    routeName: "",
    routePath: "",
    generatedTestcaseItems: [],
    repeatTestcaseItems: [],
    noUseTestcaseItems: [],
    problemModelSummary: "",
    inputOutputSummary: "",
    dataBoundsSummary: "",
    createdAt: "",
    updatedAt: "",
    completedAt: "",
    canceledAt: "",
    notifiedAt: "",
    notificationShownAt: "",
    cardDismissedAt: ""
  };
}

export function getAiTestcaseStorageKey(contestId, problemId) {
  return AI_TESTCASE_TASK_STORAGE_PREFIX + (contestId || "unknown") + "_" + (problemId || "draft");
}

export function dispatchAiTestcaseTaskUpdate() {
  if (typeof window === "undefined" || !window.dispatchEvent) {
    return;
  }
  let event;
  if (typeof Event === "function") {
    event = new Event(AI_TESTCASE_TASK_UPDATE_EVENT);
  } else {
    event = document.createEvent("Event");
    event.initEvent(AI_TESTCASE_TASK_UPDATE_EVENT, false, false);
  }
  window.dispatchEvent(event);
}

export function dispatchAiTestcaseReviewRequest(task) {
  if (typeof window === "undefined" || !window.dispatchEvent) {
    return;
  }
  let event;
  if (typeof CustomEvent === "function") {
    event = new CustomEvent(AI_TESTCASE_REVIEW_EVENT, { detail: task || {} });
  } else {
    event = document.createEvent("CustomEvent");
    event.initCustomEvent(AI_TESTCASE_REVIEW_EVENT, false, false, task || {});
  }
  window.dispatchEvent(event);
}

export function getAiTestcaseTaskKeys() {
  const keys = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (key && key.indexOf(AI_TESTCASE_TASK_STORAGE_PREFIX) === 0) {
      keys.push(key);
    }
  }
  return keys;
}

export function parseAiTestcaseTask(raw) {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export function readAiTestcaseTask(storageKey) {
  if (!storageKey) {
    return null;
  }
  return parseAiTestcaseTask(localStorage.getItem(storageKey));
}

export function writeAiTestcaseTask(storageKey, task, shouldNotify) {
  if (!storageKey || !task) {
    return;
  }
  localStorage.setItem(storageKey, JSON.stringify(task));
  if (shouldNotify !== false) {
    dispatchAiTestcaseTaskUpdate();
  }
}

export function removeAiTestcaseTask(storageKey, shouldNotify) {
  if (!storageKey) {
    return;
  }
  localStorage.removeItem(storageKey);
  if (shouldNotify !== false) {
    dispatchAiTestcaseTaskUpdate();
  }
}

export function getAiTestcaseTaskKeyInfo(storageKey) {
  const suffix = storageKey.replace(AI_TESTCASE_TASK_STORAGE_PREFIX, "");
  const parts = suffix.split("_");
  const problemId = parts.slice(1).join("_");
  return {
    contestId: parts[0] || "",
    problemId: problemId === "draft" ? "" : problemId
  };
}

export function isAiTestcaseTaskPending(status) {
  return status === "PENDING" || status === "QUEUED" || status === "RUNNING";
}

export function isAiTestcaseTaskCanceled(status) {
  return status === "CANCELED";
}

export function isAiTestcaseTaskFinished(status) {
  return status === "SUCCESS" || status === "FAILED" || status === "EXPIRED" || status === "CANCELED";
}

export function isAiTestcaseTaskFinishedWithError(status) {
  return status === "FAILED" || status === "EXPIRED";
}

export function getAiTestcaseTaskProblemLabel(task) {
  const problemNumber = task && task.problemNumber ? task.problemNumber : "";
  const problemName = task && task.problemName ? task.problemName : "";
  if (problemNumber && problemName) {
    return "题目 " + problemNumber + " - " + problemName;
  }
  if (problemName) {
    return "题目《" + problemName + "》";
  }
  if (problemNumber) {
    return "题目 " + problemNumber;
  }
  return "当前题目";
}

export function normalizeAiTestcaseTask(storageKey, cachedTask, resultData) {
  const keyInfo = getAiTestcaseTaskKeyInfo(storageKey);
  const source = resultData || {};
  const cachedTaskId = cachedTask && cachedTask.taskId ? cachedTask.taskId : "";
  const sourceTaskId = source.taskId || "";
  const isSameTask = !cachedTaskId || !sourceTaskId || cachedTaskId === sourceTaskId;
  const task = Object.assign({}, cachedTask || {}, source);
  if (!isSameTask) {
    task.completedAt = source.completedAt || "";
    task.canceledAt = source.canceledAt || "";
    task.notifiedAt = source.notifiedAt || "";
    task.notificationShownAt = source.notificationShownAt || "";
    task.cardDismissedAt = source.cardDismissedAt || "";
  }
  task.contestId = source.contestId || task.contestId || keyInfo.contestId || "";
  task.problemId = source.problemId || task.problemId || keyInfo.problemId || "";
  task.problemName = source.problemName || task.problemName || "";
  task.problemNumber = source.problemNumber || task.problemNumber || "";
  task.routeName = source.routeName || task.routeName || "NewQuestion";
  task.routePath = source.routePath || task.routePath || "";
  task.generatedTestcaseItems = source.generatedTestcaseItems || task.generatedTestcaseItems || [];
  task.repeatTestcaseItems = source.repeatTestcaseItems || task.repeatTestcaseItems || [];
  task.noUseTestcaseItems = source.noUseTestcaseItems || source.abandonTestcaseItems || source.nouseTestcaseItems || task.noUseTestcaseItems || [];
  task.updatedAt = source.updatedAt || new Date().getTime();
  task.canceledAt = source.canceledAt || task.canceledAt || "";
  writeAiTestcaseTask(storageKey, task, false);
  return task;
}

export function markAiTestcaseTaskNotified(storageKey, task) {
  const nextTask = Object.assign({}, task, {
    notifiedAt: task.notifiedAt || new Date().getTime(),
    notificationShownAt: task.notificationShownAt || new Date().getTime()
  });
  writeAiTestcaseTask(storageKey, nextTask, false);
  return nextTask;
}

export function findAiTestcaseTaskStorageKey(taskId) {
  return getAiTestcaseTaskKeys().find(key => {
    const task = readAiTestcaseTask(key);
    return task && task.taskId === taskId;
  });
}

export function dismissAiTestcaseTask(task) {
  if (!task || !task.taskId) {
    return null;
  }
  const storageKey = task.storageKey || findAiTestcaseTaskStorageKey(task.taskId);
  const storedTask = readAiTestcaseTask(storageKey);
  if (!storageKey || !storedTask || !storedTask.taskId) {
    return null;
  }
  storedTask.cardDismissedAt = new Date().getTime();
  writeAiTestcaseTask(storageKey, storedTask, false);
  return storedTask;
}

export function getPersistedAiTestcaseTask(storageKey, taskId) {
  const task = readAiTestcaseTask(storageKey);
  if (task && (!taskId || task.taskId === taskId)) {
    return task;
  }
  return null;
}



// WEBPACK FOOTER //
// ./src/common/aiTestcaseTaskStore.js